import React from 'react'
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  CircularProgress
} from '@mui/material'

import { useNavigate } from 'react-router-dom'
import { useProducts } from '../hooks/useProducts'
import { formatCurrency } from '../utils'

const ProductsPage: React.FC = () => {
  const navigate = useNavigate()
  const { products, loading, error } = useProducts({ status: 'active' })

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading products...
        </Typography>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h6" color="error" sx={{ mt: 2 }}>
          Error loading products: {error}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please check your internet connection or try again later.
        </Typography>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
          All Products
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Discover our complete range of solar products and solutions
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {products.map((product) => (
          <Grid item xs={12} sm={6} md={4} key={product.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ position: 'relative' }}>
                <Chip
                  label={formatCurrency(product.price)}
                  color="primary"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    zIndex: 1,
                    fontWeight: 600,
                  }}
                />
                <CardMedia
                  component="img"
                  height="200"
                  image={product.media?.images?.[0] || 'https://via.placeholder.com/300x200?text=No+Image'}
                  alt={product.name}
                  sx={{ objectFit: 'cover' }}
                />
              </Box>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {product.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {product.shortDescription || product.description}
                </Typography>
                <Chip
                  label={typeof product.category === 'string' ? product.category : product.category?.main || 'Uncategorized'}
                  variant="outlined"
                  size="small"
                  sx={{ mb: 2 }}
                />
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={() => navigate(`/products/${product.id}`)}
                >
                  View Details
                </Button>
                <Button variant="contained" size="small" fullWidth>
                  Add to Cart
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {products.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No products found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Check back later for new products
          </Typography>
        </Box>
      )}
    </Container>
  )
}

export default ProductsPage